'use client'

import { Card, CardContent } from '@/components/card'
import { Label } from '@/components/label'
import { RadioGroup } from '@/components/radiogroup'
import { RadioGroupItem } from '@/components/radiogroupitem'
import { cn } from '@/utils/common'
import { getImageUrl } from '@/utils/getImageUrl'
import * as React from 'react'

interface LikertScaleQuestionProps {
  question: string
  leftLabel?: string
  rightLabel?: string
  points?: number
  selectedValue?: number
  setSelectedValue: (value: number) => void
  onChange?: (value: number) => void
  className?: string
  timeLimitSeconds?: number | null
  onTimeLimitReached?: () => void
  pointDetails?: {
    value: number
    imageFileId?: string | null
    text?: string | null
  }[]
}

export function LikertScaleQuestion({
  question,
  leftLabel = 'Zdecydowanie się nie zgadzam',
  rightLabel = 'Zdecydowanie się zgadzam',
  points = 5,
  selectedValue,
  setSelectedValue,
  onChange,
  className,
  timeLimitSeconds,
  onTimeLimitReached,
  pointDetails = []
}: LikertScaleQuestionProps) {
  const [remainingTime, setRemainingTime] = React.useState<number | null>(
    timeLimitSeconds ?? null
  )

  React.useEffect(() => {
    // Reset timer when timeLimitSeconds changes
    setRemainingTime(timeLimitSeconds ?? null)
  }, [timeLimitSeconds])

  React.useEffect(() => {
    if (!timeLimitSeconds) return

    const timer = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev === null || prev <= 0) {
          clearInterval(timer)
          onTimeLimitReached?.()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [timeLimitSeconds, onTimeLimitReached])

  const handleSelect = (val: number) => {
    setSelectedValue(val)
    onChange?.(val)
  }

  const scaleOptions = React.useMemo(
    () =>
      Array.from({ length: points }, (_, i) => {
        const value = i + 1
        const details = pointDetails.find((d) => d.value === value)
        return {
          value,
          label: `${value}`,
          imageFileId: details?.imageFileId,
          text: details?.text
        }
      }),
    [points, pointDetails]
  )

  const shouldShowCards = React.useMemo(() => {
    if (pointDetails.length === 0) return false
    const showCards = pointDetails.some((a) => {
      return a.imageFileId != null || a.text != null
    })
    return showCards
  }, [pointDetails])

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  return (
    <div className="relative">
      {remainingTime !== null && (
        <div className="absolute -top-8 right-0 rounded-md bg-transparent px-3 py-1 text-sm font-medium text-gray-600">
          Pozostały czas: {formatTime(remainingTime)}
        </div>
      )}
      <Card className={cn('mx-auto md:w-[80vw] xl:w-[40vw]', className)}>
        <CardContent className="pt-4 sm:pt-6">
          <div className="space-y-4 sm:space-y-8">
            <h1 className="px-2 text-center text-lg font-bold sm:text-xl">{question}</h1>
            <div className="flex flex-col gap-4 sm:gap-6">
              {shouldShowCards ? (
                <div className={'grid grid-cols-2 gap-4 md:grid-cols-5'}>
                  {scaleOptions.map((option) => (
                    <div
                      className={`flex cursor-pointer flex-col items-center rounded-lg border-2 p-4 transition-all ${
                        selectedValue === option.value
                          ? 'border-[#2bcaca] bg-indigo-50'
                          : 'border-gray-200 bg-white hover:border-[#2bcaca]/70'
                      }`}
                      key={option.value}
                      onClick={() => handleSelect(option.value)}>
                      {option.imageFileId && (
                        <div className="mb-3 aspect-square w-full">
                          <img
                            alt={`Opcja ${option.value}`}
                            className="h-full w-full object-contain"
                            src={getImageUrl(option.imageFileId)}
                          />
                        </div>
                      )}
                      <div className="flex h-full items-center justify-center text-center text-sm text-gray-700">
                        {option.text}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <RadioGroup
                  className="flex flex-col gap-4"
                  onValueChange={(val) => handleSelect(Number(val))}
                  value={selectedValue?.toString() || ''}>
                  {/* Desktop horizontal scale */}
                  <div className="hidden w-full flex-col px-2 py-2 sm:px-4 md:flex">
                    <div className="mb-4 flex w-full items-center sm:mb-6">
                      {/* Left label */}
                      <div className="w-[100px] pr-2 text-right text-xs text-gray-600 sm:w-[120px] sm:pr-4 sm:text-sm">
                        {leftLabel}
                      </div>

                      {/* Scale container */}
                      <div className="relative mx-2 flex-1 sm:mx-4">
                        {/* Track line */}
                        <div className="absolute top-1/2 h-1 w-full -translate-y-1/2 rounded-full bg-gray-200"></div>

                        {/* Scale points */}
                        <div className="relative flex w-full items-center justify-between">
                          {scaleOptions.map((option) => (
                            <div
                              className="relative z-10 flex items-center justify-center"
                              key={option.value}
                              onClick={() => handleSelect(option.value)}>
                              <div
                                className={`flex h-5 w-5 cursor-pointer items-center justify-center rounded-full sm:h-6 sm:w-6 ${
                                  selectedValue === option.value
                                    ? 'border-2 border-indigo-200 bg-[#2bcaca] shadow-md'
                                    : 'border-2 border-gray-300 bg-white hover:border-indigo-300'
                                } transition-all duration-200`}>
                                {selectedValue === option.value && (
                                  <div className="h-1.5 w-1.5 rounded-full bg-white sm:h-2 sm:w-2" />
                                )}
                              </div>
                              <RadioGroupItem
                                className="sr-only"
                                id={`desktop-option-${option.value}`}
                                value={option.value.toString()}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Right label */}
                      <div className="w-[100px] pl-2 text-xs text-gray-600 sm:w-[120px] sm:pl-4 sm:text-sm">
                        {rightLabel}
                      </div>
                    </div>
                  </div>

                  {/* Mobile vertical scale */}
                  <div className="flex flex-col gap-2 sm:gap-3 md:hidden">
                    {scaleOptions.map((option) => (
                      <div
                        className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors sm:gap-4 sm:p-4 ${
                          selectedValue === option.value
                            ? 'border-indigo-300 bg-indigo-50'
                            : 'hover:bg-gray-50'
                        }`}
                        key={option.value}
                        onClick={() => handleSelect(option.value)}>
                        <RadioGroupItem
                          className="h-4 w-4 sm:h-5 sm:w-5"
                          id={`mobile-option-${option.value}`}
                          value={option.value.toString()}
                        />
                        <Label
                          className="flex-1 cursor-pointer text-sm sm:text-base"
                          htmlFor={`mobile-option-${option.value}`}>
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
